/**
 * Support.
 *
 * Tickets, and the service level position against each one. A central team
 * running a service for several entities needs to see everything raised across
 * the group, which is what the central support oversight feature switches on.
 */

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useTenant } from '../lib/tenant';
import { dateTime, relativeTime, statusLabel, statusTone } from '../lib/format';
import {
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  ErrorNotice,
  Modal,
  PageHeader,
  Select,
  Stat,
  TextArea,
  TextInput,
} from '../components/ui';

interface Ticket {
  id: string;
  ticket_no: string;
  subject: string;
  description: string;
  category: string;
  severity: string;
  status: string;
  raised_by_name: string | null;
  entity_name: string | null;
  raised_at: string;
  response_due_at: string | null;
  resolution_due_at: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
}

export function SupportPage() {
  const { configuration, featureEnabled } = useTenant();
  const [raising, setRaising] = useState(false);

  const tickets = useAsync(() => api.get<{ tickets: Ticket[] }>('/operations/support/tickets'), []);

  const support = configuration?.support;

  return (
    <>
      <PageHeader
        title="Support"
        subtitle={support ? `${support.deskName} · ${support.hours}` : undefined}
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setRaising(true)}>
            Raise a ticket
          </button>
        }
      />

      <AsyncPanel state={tickets}>
        {(data) => {
          const open = data.tickets.filter((t) => !['resolved', 'closed'].includes(t.status));
          const breached = open.filter(
            (t) => t.resolution_due_at && new Date(t.resolution_due_at) < new Date(),
          );
          const awaitingResponse = open.filter((t) => !t.first_response_at);

          return (
            <div className="stack">
              <div className="grid grid--3">
                <Stat label="Open tickets" value={String(open.length)} />
                <Stat
                  label="Awaiting a first response"
                  value={String(awaitingResponse.length)}
                  tone={awaitingResponse.length > 0 ? 'negative' : undefined}
                />
                <Stat
                  label="Past the resolution target"
                  value={String(breached.length)}
                  tone={breached.length > 0 ? 'negative' : 'positive'}
                />
              </div>

              <Card
                title="Tickets"
                subtitle={
                  featureEnabled('centralSupportOversight')
                    ? 'Every ticket raised across the organisation, with its service level position.'
                    : undefined
                }
                flush
              >
                <DataTable
                  caption="Support tickets"
                  rows={data.tickets}
                  rowKey={(t) => t.id}
                  emptyMessage="No tickets have been raised."
                  columns={[
                    { key: 'no', header: 'Ticket', render: (t) => <span className="mono">{t.ticket_no}</span> },
                    {
                      key: 'subject',
                      header: 'Subject',
                      render: (t) => (
                        <>
                          <div className="truncate">{t.subject}</div>
                          <div className="smaller muted truncate">{t.description}</div>
                        </>
                      ),
                    },
                    {
                      key: 'raised',
                      header: 'Raised',
                      render: (t) => (
                        <span className="smaller">
                          {relativeTime(t.raised_at)}
                          {t.raised_by_name ? <div className="muted">{t.raised_by_name}</div> : null}
                          {t.entity_name ? <div className="muted">{t.entity_name}</div> : null}
                        </span>
                      ),
                    },
                    {
                      key: 'severity',
                      header: 'Severity',
                      render: (t) => <Badge tone={statusTone(t.severity)}>{t.severity}</Badge>,
                    },
                    {
                      key: 'response',
                      header: 'First response',
                      render: (t) =>
                        t.first_response_at ? (
                          <span className="smaller">{relativeTime(t.first_response_at)}</span>
                        ) : t.response_due_at && new Date(t.response_due_at) < new Date() ? (
                          <Badge tone="error">Overdue</Badge>
                        ) : (
                          <span className="smaller muted">
                            due {t.response_due_at ? relativeTime(t.response_due_at) : '—'}
                          </span>
                        ),
                    },
                    {
                      key: 'resolution',
                      header: 'Resolution target',
                      render: (t) =>
                        t.resolved_at ? (
                          <Badge tone="ok">Resolved</Badge>
                        ) : t.resolution_due_at && new Date(t.resolution_due_at) < new Date() ? (
                          <Badge tone="error">Past target</Badge>
                        ) : (
                          <span className="smaller muted">
                            {t.resolution_due_at ? relativeTime(t.resolution_due_at) : '—'}
                          </span>
                        ),
                    },
                    { key: 'status', header: 'Status', render: (t) => <Badge tone={statusTone(t.status)}>{statusLabel(t.status)}</Badge> },
                  ]}
                />
              </Card>

              {support ? (
                <Card title="How support works">
                  <p>{support.escalation}</p>
                  <p className="smaller muted">
                    {support.deskName} · {support.email} · {support.telephone} · {support.hours}
                  </p>
                </Card>
              ) : null}
            </div>
          );
        }}
      </AsyncPanel>

      <RaiseTicketModal
        open={raising}
        onClose={() => setRaising(false)}
        onCreated={() => {
          setRaising(false);
          tickets.reload();
        }}
      />
    </>
  );
}

function RaiseTicketModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('incident');
  const [severity, setSeverity] = useState('P3');

  const create = useMutation(async () =>
    api.post('/operations/support/tickets', {
      subject: subject.trim(),
      description: description.trim(),
      category,
      severity,
    }),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Raise a support ticket"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={create.busy || subject.trim().length < 5 || description.trim().length < 10}
            onClick={() => void create.run().then((r) => r !== null && onCreated())}
          >
            {create.busy ? 'Raising…' : 'Raise the ticket'}
          </button>
        </>
      }
    >
      <div className="stack">
        <TextInput label="Subject" value={subject} onChange={(e) => setSubject(e.currentTarget.value)} required />
        <TextArea
          label="What is happening?"
          hint="What you did, what you expected, and what happened instead."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          required
        />
        <div className="grid grid--2">
          <Select label="Category" value={category} onChange={(e) => setCategory(e.currentTarget.value)}>
            <option value="incident">Something is broken</option>
            <option value="service_request">A change or addition</option>
            <option value="question">A question</option>
          </Select>
          <Select
            label="Severity"
            hint="P1 means finance cannot operate at all."
            value={severity}
            onChange={(e) => setSeverity(e.currentTarget.value)}
          >
            <option value="P1">P1 — the system is unusable</option>
            <option value="P2">P2 — a major function is unavailable</option>
            <option value="P3">P3 — something is wrong but there is a way round it</option>
            <option value="P4">P4 — a question or a request</option>
          </Select>
        </div>
        <ErrorNotice error={create.error} />
      </div>
    </Modal>
  );
}
